import { computed, type Ref } from 'vue';
import { formatDurationHoursMinutes } from '@/composables/wage/useWageCalculator';
import type { HomeWageClaimRow } from '@/policies';
import type { HomeWagePayrollViewMode } from '@/composables/wage/useHomeWagePayrollViewMode';
import {
  calcPlanDurationHours,
  calcPremiumDetailsFromClaims,
  filterNonSevereClaimRows,
  type HomeWagePremiumHoursSummary,
} from '@/policies/homeWageSettingPolicy';

export const HOME_WAGE_WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'] as const;

export interface WorkCalendarEvent {
  startTime: string;
  recipientName: string;
  sortKey: string;
}

export interface WorkCalendarDayCell {
  day: number;
  dateKey: string | null;
  inMonth: boolean;
  isSunday: boolean;
  isSaturday: boolean;
  isHoliday: boolean;
  holidayName: string | null;
  dateLabel: string;
  events: WorkCalendarEvent[];
  /** 해당 일의 총 근무시간 */
  dayTotalHours: number;
  /** 중식대 가능일 여부 (점심시간 포함 근무) */
  isLunchDay: boolean;
  /** 일별 연·야·휴 시간 (B1/B2 plan 기준) */
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
}

export interface WorkCalendarRecipientSummary {
  recipientName: string;
  serviceCount: number;
  totalHours: number;
  /** 수가명 (가장 처음 나타난 수가) */
  feeName?: string;
  /** 총 수가금액 */
  totalFeeAmount?: number;
}

export interface WorkCalendarSummary {
  workDayCount: number;
  totalHours: number;
  serviceCount: number;
  recipientCount: number;
  /** 중식대 가능 건수 (점심시간 포함 근무 청구 건) */
  lunchEligibleCount: number;
  /** 중식대 가능일 수 (점심시간 포함 근무일 — 식대 계산용) */
  lunchWorkDayCount: number;
}

function parseClaimDateKey(date: string | null | undefined): string | null {
  if (!date) return null;
  const s = String(date).substring(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

function formatTimeHm(time: string | null | undefined): string {
  if (!time) return '--:--';
  return String(time).substring(0, 5);
}

/** 시간 문자열을 분으로 변환 (예: "12:30" → 750) */
function timeToMinutes(time: string | null | undefined): number | null {
  if (!time) return null;
  const s = String(time).substring(0, 5);
  const match = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

/** 점심시간(12:00~13:00)을 포함하는 근무인지 확인 (12시 이상이면 식대 지급) */
function isLunchTimeIncluded(startTime: string | null | undefined, endTime: string | null | undefined): boolean {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (start == null || end == null) return false;
  const lunchStart = 12 * 60; // 12:00
  const lunchEnd = 13 * 60;   // 13:00
  // 종료시간이 12:00 이상이면 식대 지급 (12시 초과가 아닌 12시 이상)
  return start < lunchEnd && end >= lunchStart;
}

/** 총 근무시간 표시 — n시간 n분 (예: 2시간 30분, 40분) */
export function formatWorkCalendarDuration(totalHours: number): string {
  return formatDurationHoursMinutes(totalHours) || '0분';
}

function buildDateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

interface DayStats {
  totalHours: number;
  isLunchDay: boolean;
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
}

function makeDayCell(
  year: number,
  month: number,
  day: number,
  inMonth: boolean,
  eventsByDate: Map<string, WorkCalendarEvent[]>,
  holidayMap: Record<string, string>,
  dayStatsMap: Map<string, DayStats>
): WorkCalendarDayCell {
  const dateKey = buildDateKey(year, month, day);
  const dow = new Date(year, month - 1, day).getDay();
  const holidayName = holidayMap[dateKey] || null;
  const isHoliday = !!holidayName;
  const dateLabel = day === 1 ? `${month}/${day}` : String(day);
  const stats = dayStatsMap.get(dateKey) || {
    totalHours: 0,
    isLunchDay: false,
    overtimeHours: 0,
    nightHours: 0,
    holidayHours: 0,
  };
  return {
    day,
    dateKey,
    inMonth,
    isSunday: dow === 0,
    isSaturday: dow === 6,
    isHoliday,
    holidayName,
    dateLabel,
    events: eventsByDate.get(dateKey) || [],
    dayTotalHours: stats.totalHours,
    isLunchDay: stats.isLunchDay,
    overtimeHours: stats.overtimeHours,
    nightHours: stats.nightHours,
    holidayHours: stats.holidayHours,
  };
}

function buildMonthWeeks(
  year: number,
  month: number,
  eventsByDate: Map<string, WorkCalendarEvent[]>,
  holidayMap: Record<string, string>,
  dayStatsMap: Map<string, DayStats>
): WorkCalendarDayCell[][] {
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow = new Date(year, month - 1, 1).getDay();
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  const prevMonthDays = new Date(prevYear, prevMonth, 0).getDate();
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;

  const weeks: WorkCalendarDayCell[][] = [];
  let week: WorkCalendarDayCell[] = [];

  for (let i = 0; i < firstDow; i++) {
    const d = prevMonthDays - firstDow + i + 1;
    week.push(makeDayCell(prevYear, prevMonth, d, false, eventsByDate, holidayMap, dayStatsMap));
  }

  for (let d = 1; d <= daysInMonth; d++) {
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
    week.push(makeDayCell(year, month, d, true, eventsByDate, holidayMap, dayStatsMap));
  }

  let nextDay = 1;
  while (week.length > 0 && week.length < 7) {
    week.push(makeDayCell(nextYear, nextMonth, nextDay++, false, eventsByDate, holidayMap, dayStatsMap));
  }
  if (week.length) weeks.push(week);

  return weeks;
}

/**
 * 청구내역 → 근무달력(월간 그리드·요약·수급자별 집계)
 */
export function useHomeWageWorkCalendar(options: {
  claimRows: Ref<HomeWageClaimRow[]>;
  year: Ref<number>;
  month: Ref<number>;
  /** YYYY-MM-DD → 공휴일명 (caion_home_holiday) */
  holidayMap: Ref<Record<string, string>>;
  /** 센터장/관리자 시점 — 휴일수당(plan) 집계 기준 */
  viewMode?: Ref<HomeWagePayrollViewMode>;
}) {
  /** ★ 유효한 청구내역만 (청구내역+청구내역상세 INNER JOIN된 것 = 수가명/수가코드 있는 행) */
  const validClaimRows = computed(() => {
    return options.claimRows.value.filter((row) => {
      const feeName = String(row.fee_name || '').trim();
      const feeCode = String(row.fee_code || '').trim();
      return feeName.length > 0 || feeCode.length > 0;
    });
  });

  /** 근무달력·중식·서비스횟수 집계용 — 중증수급자가산 제외 */
  const serviceClaimRows = computed(() => filterNonSevereClaimRows(validClaimRows.value));

  const eventsByDate = computed(() => {
    const map = new Map<string, WorkCalendarEvent[]>();
    // ★ 중증수급자가산 제외 — 실제 서비스만 달력 표시
    for (const row of serviceClaimRows.value) {
      const dateKey = parseClaimDateKey(row.date);
      if (!dateKey) continue;
      const recipientName = String(row.recipient_name || '').trim() || '-';
      const startTime = formatTimeHm(row.plan_start_time);
      const event: WorkCalendarEvent = {
        startTime,
        recipientName,
        sortKey: `${startTime}-${recipientName}`,
      };
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(event);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.sortKey.localeCompare(b.sortKey, 'ko'));
    }
    return map;
  });

  /** 일별 통계 (총 근무시간, 중식대, 연·야·휴) */
  const premiumYearMonth = computed(
    () => `${options.year.value}${String(options.month.value).padStart(2, '0')}`
  );

  const premiumDetails = computed(() =>
    calcPremiumDetailsFromClaims(
      serviceClaimRows.value,
      premiumYearMonth.value,
      options.viewMode?.value ?? 'admin'
    )
  );

  const premiumSummary = computed<HomeWagePremiumHoursSummary>(
    () => premiumDetails.value.summary
  );

  const overtimeDayKeys = computed(() => premiumDetails.value.overtimeDayKeys);
  const overtimeRowKeys = computed(() => premiumDetails.value.overtimeRowKeys);

  const dayStatsMap = computed(() => {
    const map = new Map<string, DayStats>();
    // ★ 중증수급자가산 제외 — 근무시간·중식 집계
    for (const row of serviceClaimRows.value) {
      const dateKey = parseClaimDateKey(row.date);
      if (!dateKey) continue;
      const hours = calcPlanDurationHours(row.plan_start_time, row.plan_end_time);
      const hasLunchOverlap = isLunchTimeIncluded(row.plan_start_time, row.plan_end_time);
      const prev = map.get(dateKey) || {
        totalHours: 0,
        isLunchDay: false,
        overtimeHours: 0,
        nightHours: 0,
        holidayHours: 0,
      };
      map.set(dateKey, {
        ...prev,
        totalHours: prev.totalHours + hours,
        isLunchDay: prev.isLunchDay || hasLunchOverlap,
      });
    }
    // 연·야·휴 일별 시간 병합
    for (const day of premiumDetails.value.byDate) {
      const prev = map.get(day.dateKey) || {
        totalHours: 0,
        isLunchDay: false,
        overtimeHours: 0,
        nightHours: 0,
        holidayHours: 0,
      };
      map.set(day.dateKey, {
        ...prev,
        overtimeHours: day.overtimeHours,
        nightHours: day.nightHours,
        holidayHours: day.holidayHours,
      });
    }
    return map;
  });

  const calendarWeeks = computed(() =>
    buildMonthWeeks(
      options.year.value,
      options.month.value,
      eventsByDate.value,
      options.holidayMap.value,
      dayStatsMap.value
    )
  );

  const summary = computed<WorkCalendarSummary>(() => {
    // ★ 중증수급자가산 제외 — 근로일·서비스횟수·근무시간 집계
    const rows = serviceClaimRows.value;
    const workDays = new Set<string>();
    let totalHours = 0;
    const recipients = new Set<string>();
    let lunchWorkDayCount = 0;
    let lunchEligibleCount = 0;

    for (const row of rows) {
      const dateKey = parseClaimDateKey(row.date);
      if (dateKey) workDays.add(dateKey);
      totalHours += calcPlanDurationHours(row.plan_start_time, row.plan_end_time);
      const name = String(row.recipient_name || '').trim();
      if (name) recipients.add(name);
      if (isLunchTimeIncluded(row.plan_start_time, row.plan_end_time)) {
        lunchEligibleCount++;
      }
    }

    for (const dateKey of workDays) {
      const stats = dayStatsMap.value.get(dateKey);
      if (stats?.isLunchDay) lunchWorkDayCount++;
    }

    return {
      workDayCount: workDays.size,
      totalHours,
      serviceCount: rows.length,
      recipientCount: recipients.size,
      lunchEligibleCount,
      lunchWorkDayCount,
    };
  });

  const recipientSummaries = computed<WorkCalendarRecipientSummary[]>(() => {
    const map = new Map<string, { count: number; hours: number; feeName: string; feeAmount: number }>();
    for (const row of serviceClaimRows.value) {
      const name = String(row.recipient_name || '').trim() || '-';
      const hours = calcPlanDurationHours(row.plan_start_time, row.plan_end_time);
      const feeName = String(row.fee_name || '').trim();
      const feeAmount = Number(row.fee_amount) || 0;
      const prev = map.get(name) || { count: 0, hours: 0, feeName: '', feeAmount: 0 };
      map.set(name, {
        count: prev.count + 1,
        hours: prev.hours + hours,
        feeName: prev.feeName || feeName,
        feeAmount: prev.feeAmount + feeAmount,
      });
    }
    return Array.from(map.entries())
      .map(([recipientName, v]) => ({
        recipientName,
        serviceCount: v.count,
        totalHours: v.hours,
        feeName: v.feeName,
        totalFeeAmount: v.feeAmount,
      }))
      .sort((a, b) => a.recipientName.localeCompare(b.recipientName, 'ko'));
  });

  const calendarMonthLabel = computed(
    () => `${options.year.value}년 ${options.month.value}월`
  );

  return {
    weekdayLabels: HOME_WAGE_WEEKDAY_LABELS,
    calendarWeeks,
    summary,
    recipientSummaries,
    calendarMonthLabel,
    premiumSummary,
    overtimeDayKeys,
    overtimeRowKeys,
    formatWorkCalendarDuration,
  };
}
